import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  message,
  Card,
  Divider,
  Typography,
  Empty,
  Badge,
  Alert,
  Space,
  Switch,
  InputNumber,
  Tag,
  Table,
  Modal,
  Collapse,
  Tooltip,
  Upload,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CodeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  InboxOutlined,
  TeamOutlined,
  AppstoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import PulseTemplateGallery from "@views/superAdmin/pulseReports/components/PulseTemplateGallery";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { withRouter } from "react-router";
import {
  createPulseReport,
  getAvailableVariables,
} from "@api/pulse-reports/pulseReport";
import { getHtmlTemplates, getHtmlTemplateById } from "@api/email/htmlTemplate";
import { getAllPlans } from "@api/subscription/plan";
import { getActiveUserEmails } from "@api/email/users/userEmail";
import { searchUsers } from "@api/user/superAdmin";
import { SuccessNotification, ErrorNotification, EmailVolumeControl } from "@components";
import moment from "moment-timezone";
import debounce from "lodash/debounce";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Dragger } = Upload;

// Report types with detailed helpers
const reportTypes = [
  {
    value: "invoice",
    label: "Invoice Report",
    description: "Send billing and invoice details",
    helper: "Best for: Monthly billing summaries. Includes plan name, amount, payment date, invoice PDF link. Typically sent on_refill or monthly."
  },
  {
    value: "usage",
    label: "Usage Report",
    description: "Send usage metrics and chat consumption",
    helper: "Best for: Keeping users informed about limits. Shows chats used/remaining, storage metrics, usage trends. Good for weekly sends."
  },
  {
    value: "performance",
    label: "Performance Report",
    description: "Chatbot performance statistics",
    helper: "Best for: Demonstrating ROI. Includes conversation counts, avg duration, top performing bot, conversion rates. Great for weekly/monthly."
  },
  {
    value: "analytics",
    label: "Analytics Report",
    description: "Detailed analytics and insights",
    helper: "Best for: Traffic insights. Shows visitors, top sources, countries, device breakdown, peak hours. Ideal for weekly digests."
  },
  {
    value: "digest",
    label: "Weekly/Monthly Digest",
    description: "Summary of all activities",
    helper: "Best for: Comprehensive updates. Combines multiple data sections into one report. Perfect for weekly or monthly summaries."
  },
  {
    value: "newsletter",
    label: "Newsletter",
    description: "Marketing and updates",
    helper: "Best for: Announcements to users. Uses custom variables you define. Great for news, tips, and promotional content."
  },
  {
    value: "product_update",
    label: "Product Update",
    description: "Product updates and announcements",
    helper: "Best for: Feature releases. Announce new features, updates, improvements. Usually sent manually when needed."
  },
  {
    value: "custom",
    label: "Custom Report",
    description: "Custom data sections",
    helper: "Best for: Flexible needs. Mix and match any data sections. Define your own variables for complete customization."
  },
];

// Schedule types with detailed helpers
const scheduleTypes = [
  {
    value: "on_refill",
    label: "On Subscription Refill",
    description: "Triggered when subscription renews",
    helper: "Automatically sends 1-24 hours after billing cycle completes. Perfect for invoice reports. Delay configurable in settings."
  },
  {
    value: "weekly",
    label: "Weekly",
    description: "Sent on specific day each week",
    helper: "Choose which day (Mon-Sun) and time. Great for performance digests, usage updates. Keeps users regularly engaged."
  },
  {
    value: "monthly",
    label: "Monthly",
    description: "Sent on specific day each month",
    helper: "Choose day (1-28) and time. Ideal for monthly summaries, analytics reports. Use day 1 for start-of-month, 28 for end-of-month."
  },
  {
    value: "quarterly",
    label: "Quarterly",
    description: "Sent every 3 months",
    helper: "Sends on configured months (Jan, Apr, Jul, Oct). Best for business reviews, comprehensive analytics summaries."
  },
  {
    value: "manual",
    label: "Manual Only",
    description: "Only sent manually by admin",
    helper: "No automatic scheduling. Use 'Send Now' button when ready. Perfect for newsletters, product updates, announcements."
  },
];

// Days of week
const daysOfWeek = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Target types with detailed helpers
const targetTypes = [
  {
    value: "all",
    label: "All Users",
    description: "Send to all registered users",
    helper: "Sends to every active user in the system. You can still exclude specific users. Best for platform-wide announcements."
  },
  {
    value: "planBased",
    label: "Users by Plan",
    description: "Filter by subscription plan",
    helper: "Target users on specific plans (Free, Optimizer, Wizard, Maverick). Great for plan-specific features or upgrade prompts."
  },
  {
    value: "targeted",
    label: "Specific Users",
    description: "Select individual users",
    helper: "Hand-pick specific users by searching name/email. Perfect for VIP communications or testing reports."
  },
  {
    value: "dateRange",
    label: "Users by Join Date",
    description: "Users who joined in date range",
    helper: "Target users who registered between specific dates. Great for cohort-based campaigns or onboarding sequences."
  },
  {
    value: "external",
    label: "External Recipients",
    description: "Import external email recipients",
    helper: "Send to non-registered emails via CSV/Excel import. Each row becomes a recipient with custom variables. Perfect for newsletters to prospects."
  },
];

// Initial form state
const initialState = {
  name: "",
  description: "",
  reportType: "invoice",
  htmlTemplate: "",
  targetType: "all",
  includedPlans: [],
  excludedUsers: [],
  targetedUsers: [],
  externalRecipients: [],
  scheduleType: "monthly",
  dayOfWeek: 1,
  dayOfMonth: 1,
  timeHour: 9,
  timeMinute: 0,
  timezone: moment.tz.guess(),
  emailSubject: "",
  fromName: "Copilotly",
  replyTo: "",
  emailsToUse: [],
  cc: [],
  bcc: [],
  variables: [],
  // Data sections
  billingEnabled: true,
  usageEnabled: true,
  performanceEnabled: false,
  analyticsEnabled: false,
  teamEnabled: false,
  // Email sending config
  emailSendingConfig: {
    sendingMode: "time_based",
    minutesBetweenEmails: 5,
    emailsPerMinute: 20,
    batchSize: 50,
  },
};

// Variable form modal
const VariableForm = ({ variable, onSave, onCancel }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (variable) {
      form.setFieldsValue(variable);
    } else {
      form.resetFields();
    }
  }, [variable, form]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave(values);
      form.resetFields();
    });
  };

  return (
    <Modal
      title={variable ? "Edit Variable" : "Add Variable"}
      open={!!variable || variable === null}
      onOk={handleSave}
      onCancel={onCancel}
      okText="Save"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="key"
          label="Variable Key"
          rules={[
            { required: true, message: "Please enter a key" },
            { pattern: /^[a-z_][a-z0-9_]*$/, message: "Use lowercase letters, numbers, and underscores" },
          ]}
        >
          <Input placeholder="e.g., custom_greeting" />
        </Form.Item>
        <Form.Item
          name="defaultValue"
          label="Default Value"
          rules={[{ required: true, message: "Please enter a default value" }]}
        >
          <Input placeholder="e.g., Hello there!" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input placeholder="e.g., Custom greeting for users" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PulseReportCreateForm = ({ history }) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [availableEmails, setAvailableEmails] = useState({});
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [templateGalleryVisible, setTemplateGalleryVisible] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  const [templatePreviewVisible, setTemplatePreviewVisible] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [availableVars, setAvailableVars] = useState([]);
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  // External recipients state
  const [fileRecipients, setFileRecipients] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [recipientColumns, setRecipientColumns] = useState(new Set(["email", "name"]));
  const [availableColumns, setAvailableColumns] = useState([
    { key: "email", title: "Email", required: true },
    { key: "name", title: "Name", required: false },
  ]);
  const [newRecipientData, setNewRecipientData] = useState({ email: "", name: "" });

  const timezones = moment.tz.names();

  // Fetch metadata on mount
  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const [templatesRes, plansRes, emailsRes, varsRes] = await Promise.all([
        getHtmlTemplates(),
        getAllPlans(),
        getActiveUserEmails(),
        getAvailableVariables().catch(() => ({ data: { data: [] } })),
      ]);

      // API returns { htmlTemplates: [...] }
      const templatesData = templatesRes.data?.htmlTemplates || templatesRes.data?.data || templatesRes.data || [];
      setTemplates(Array.isArray(templatesData) ? templatesData : []);

      // Plans API might return { plans: [...] } or { data: [...] } or just [...]
      const plansData = plansRes.data?.plans || plansRes.data?.data || plansRes.data || [];
      setPlans(Array.isArray(plansData) ? plansData : []);

      setAvailableVars(varsRes.data?.data || []);

      // Group emails by domain
      const emails = emailsRes.data?.data || emailsRes.data || [];
      const emailsByDomain = emails.reduce((acc, email) => {
        const domain = email.email.split("@")[1];
        if (!acc[domain]) acc[domain] = [];
        acc[domain].push(email);
        return acc;
      }, {});
      setAvailableEmails(emailsByDomain);
    } catch (error) {
      console.error("Error fetching metadata:", error);
      ErrorNotification("Failed to load form data");
    }
  };

  // Format timezone display
  const formatTimezone = (tz) => {
    const offset = moment.tz(tz).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    return `${tz} (GMT${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")})`;
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  // Handle template selection from gallery
  const handleTemplateSelect = (template) => {
    // Find matching HTML template in database by suggestedHtmlTemplateId
    const matchingTemplate = templates.find(
      (t) =>
        t.templateId === template.suggestedHtmlTemplateId ||
        t.name.toLowerCase().includes(template.suggestedHtmlTemplateId.replace(/-/g, " "))
    );

    // Update form values based on selected template
    setValues((prev) => ({
      ...prev,
      name: prev.name || template.name,
      description: prev.description || template.description,
      reportType: template.reportType,
      htmlTemplate: matchingTemplate?._id || prev.htmlTemplate,
      scheduleType: template.defaultSchedule.type === "on_event" ? "on_refill" : template.defaultSchedule.type,
      dayOfWeek: template.defaultSchedule.dayOfWeek ?? prev.dayOfWeek,
      dayOfMonth: template.defaultSchedule.dayOfMonth ?? prev.dayOfMonth,
      scheduleTime: template.defaultSchedule.time || prev.scheduleTime,
      billingEnabled: template.defaultDataSections.billing,
      usageEnabled: template.defaultDataSections.usage,
      performanceEnabled: template.defaultDataSections.performance,
      analyticsEnabled: template.defaultDataSections.analytics,
      teamEnabled: template.defaultDataSections.team,
    }));

    setSelectedTemplateName(template.name);
    message.success(`Template "${template.name}" applied! Review and customize the settings.`);
  };

  // Handle HTML template preview
  const handleTemplatePreview = async () => {
    if (!values.htmlTemplate) {
      message.warning("Please select an HTML template first");
      return;
    }

    setTemplatePreviewLoading(true);
    setTemplatePreviewVisible(true);

    try {
      const res = await getHtmlTemplateById(values.htmlTemplate);
      setTemplatePreviewData(res.data);
    } catch (error) {
      console.error("Error fetching template for preview", error);
      message.error("Failed to load template preview");
      setTemplatePreviewVisible(false);
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  // Format emails for select dropdown
  const formatEmailsForSelect = () => {
    return Object.entries(availableEmails).map(([domain, emails]) => ({
      label: (
        <Space>
          <span>Domain: {domain}</span>
          <Text type="secondary">({emails.length} emails)</Text>
        </Space>
      ),
      options: emails.flatMap((email) => {
        const baseOption = {
          value: `email:${email._id}`,
          label: (
            <Space>
              <MailOutlined />
              <span>{email.name || email.email.split("@")[0]}</span>
              <Text type="secondary">&lt;{email.email}&gt;</Text>
            </Space>
          ),
          email: email,
        };

        const aliasOptions = (email.aliases || []).map((alias) => ({
          value: `alias:${email._id}:${alias}`,
          label: (
            <Space style={{ paddingLeft: 24 }}>
              <UserOutlined />
              <span>{alias.split("@")[0]}</span>
              <Text type="secondary">&lt;{alias}&gt;</Text>
              <Badge
                count="Alias"
                style={{
                  backgroundColor: "#e6f4ff",
                  color: "#1890ff",
                  fontSize: "12px",
                }}
              />
            </Space>
          ),
          isAlias: true,
        }));

        return [baseOption, ...aliasOptions];
      }),
    }));
  };

  // Handle email selection
  const handleEmailSelection = (selected) => {
    setSelectedEmails(selected);

    // Convert to emailsToUse format
    const emailsToUse = selected.map((sel) => {
      const [type, emailId, alias] = sel.split(":");
      return {
        emailId,
        useAlias: type === "alias",
        selectedAlias: alias || null,
      };
    });

    handleChange("emailsToUse", emailsToUse);
  };

  // Get selected emails details
  const getSelectedDetails = () => {
    let baseEmails = 0;
    let aliases = 0;
    const domains = new Set();

    selectedEmails.forEach((sel) => {
      const [type, emailId] = sel.split(":");
      if (type === "email") {
        baseEmails++;
        // Find the email to get domain
        Object.values(availableEmails).flat().forEach((e) => {
          if (e._id === emailId) {
            domains.add(e.email.split("@")[1]);
          }
        });
      } else {
        aliases++;
      }
    });

    return { totalEmails: selectedEmails.length, baseEmails, aliases, domains };
  };

  // User search - debounced to avoid excessive API calls
  const performUserSearch = useCallback(async (search) => {
    if (!search || search.length < 2) return;
    setSearching(true);
    try {
      const res = await searchUsers(search);
      const users = res.data?.users || res.data?.data || [];
      setUserOptions(
        users.map((u) => ({
          value: u._id,
          label: `${u.name || u.email} <${u.email}>`,
        }))
      );
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search handler - waits 300ms after user stops typing
  const handleUserSearch = useMemo(
    () => debounce((search) => performUserSearch(search), 300),
    [performUserSearch]
  );

  // Handle CC/BCC
  const handleCcAdd = () => {
    if (!ccInput) return;
    const emails = ccInput.split(/[,\s]+/).filter((e) => e.includes("@"));
    handleChange("cc", [...new Set([...values.cc, ...emails])]);
    setCcInput("");
  };

  const handleBccAdd = () => {
    if (!bccInput) return;
    const emails = bccInput.split(/[,\s]+/).filter((e) => e.includes("@"));
    handleChange("bcc", [...new Set([...values.bcc, ...emails])]);
    setBccInput("");
  };

  // ============================================
  // EXTERNAL RECIPIENTS HANDLING
  // ============================================
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const parseCSVData = (data) => {
    if (!data || data.length === 0) return [];
    const headers = data[0].map((header) => {
      const displayName = header.trim();
      const key = header.toLowerCase().trim();
      return { key, title: displayName, required: key === "email" };
    });

    const newColumns = new Set([...recipientColumns]);
    headers.forEach((header) => newColumns.add(header.key));
    setRecipientColumns(newColumns);
    setAvailableColumns((prevColumns) => {
      const existingKeys = new Set(prevColumns.map((col) => col.key));
      const newHeaders = headers.filter((header) => !existingKeys.has(header.key));
      return [...prevColumns, ...newHeaders];
    });

    const recipients = [];
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const recipient = {};
      headers.forEach((header, index) => {
        const value = row[index]?.toString().trim() || "";
        if (value) recipient[header.key] = value;
      });
      if (recipient.email && isValidEmail(recipient.email)) {
        recipients.push(recipient);
      }
    }
    return recipients;
  };

  const handleParsedRecipients = (recipients, fileName) => {
    const formattedRecipients = recipients.map((recipient) => {
      const { email, ...otherFields } = recipient;
      const recipientValues = {};
      Object.entries(otherFields).forEach(([key, value]) => {
        if (value) recipientValues[key] = value;
      });
      return { email: email.toLowerCase(), values: recipientValues };
    });

    setFileRecipients((prev) => ({ ...prev, [fileName]: formattedRecipients }));
    setValues((prevValues) => {
      const allRecipients = [...Object.values({ ...fileRecipients, [fileName]: formattedRecipients }).flat()];
      const uniqueRecipients = allRecipients.reduce((acc, current) => {
        if (!acc.find((item) => item.email === current.email)) acc.push(current);
        return acc;
      }, []);
      return { ...prevValues, externalRecipients: uniqueRecipients };
    });

    setNewRecipientData((prevData) => {
      const newData = { ...prevData };
      Array.from(recipientColumns).forEach((column) => {
        if (!newData.hasOwnProperty(column)) newData[column] = "";
      });
      return newData;
    });

    message.success(`${recipients.length} recipients loaded from ${fileName}`);
  };

  const processFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileObj = file.originFileObj || file;
      if (fileObj instanceof Blob) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const content = e.target.result;
          let parsedData;
          try {
            if (file.name.endsWith(".csv")) {
              parsedData = Papa.parse(content, { skipEmptyLines: true, header: false }).data;
              const recipients = parseCSVData(parsedData);
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.match(/\.(xlsx|xls)$/i)) {
              const workbook = XLSX.read(content, { type: "array" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
              const recipients = parseCSVData(parsedData);
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.endsWith(".txt")) {
              const lines = content.split(/\r?\n/).filter((line) => line.trim());
              let recipients = [];
              const firstLine = lines[0].trim();
              const possibleHeaders = firstLine.split(/[,\t]/);
              if (possibleHeaders.length > 1 && possibleHeaders.some((h) => h.toLowerCase().includes("email"))) {
                parsedData = lines.map((line) => line.split(/[,\t]/));
                recipients = parseCSVData(parsedData);
              } else {
                recipients = lines.filter((line) => isValidEmail(line.trim())).map((line) => ({ email: line.trim(), name: "" }));
              }
              handleParsedRecipients(recipients, file.name);
            }
            resolve();
          } catch (error) {
            console.error("Error processing file:", error);
            message.error(`Failed to process ${file.name}. Please check the file format.`);
            reject(error);
          }
        };
        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          message.error(`Failed to read the file ${file.name}. Please try again.`);
          reject(error);
        };
        if (file.name.match(/\.(xlsx|xls)$/i)) {
          reader.readAsArrayBuffer(fileObj);
        } else {
          reader.readAsText(fileObj);
        }
      } else {
        console.error("Invalid file object:", fileObj);
        message.error(`Invalid file ${file.name}. Please try uploading again.`);
        reject(new Error("Invalid file object"));
      }
    });
  };

  const handleFileUpload = async (info) => {
    const { file, fileList } = info;
    if (file.status === "removed") {
      const updatedFileRecipients = { ...fileRecipients };
      delete updatedFileRecipients[file.name];
      setFileRecipients(updatedFileRecipients);
      const allRecipients = Object.values(updatedFileRecipients).flat();
      setValues({ ...values, externalRecipients: allRecipients });
      message.info(`File ${file.name} removed. Recipient list updated.`);
      return;
    }
    for (let i = 0; i < fileList.length; i++) {
      const currentFile = fileList[i];
      if (currentFile.status !== "done") {
        await processFile(currentFile);
      }
    }
  };

  const handleAddRecipient = () => {
    if (newRecipientData.email && isValidEmail(newRecipientData.email)) {
      const { email, ...otherFields } = newRecipientData;
      const newRecipient = { email: email.toLowerCase(), values: {} };
      Object.keys(otherFields).forEach((key) => {
        if (otherFields[key]) newRecipient.values[key] = otherFields[key];
      });
      setValues((prevValues) => ({
        ...prevValues,
        externalRecipients: Array.isArray(prevValues.externalRecipients)
          ? [...prevValues.externalRecipients, newRecipient]
          : [newRecipient],
      }));
      setNewRecipientData((prevData) => {
        const resetData = { ...prevData };
        Object.keys(resetData).forEach((key) => { resetData[key] = ""; });
        return resetData;
      });
    } else {
      message.error("Please enter a valid email address");
    }
  };

  const handleRemoveRecipient = (email) => {
    setValues({
      ...values,
      externalRecipients: values.externalRecipients.filter((r) => r.email !== email),
    });
  };

  const getTableColumns = () => {
    const columns = Array.from(recipientColumns).map((key) => ({
      title: key,
      dataIndex: key === "email" ? "email" : ["values", key],
      key: key,
      ellipsis: true,
      render: (text, record) => {
        if (key === "email") return record.email;
        return record.values?.[key] || "";
      },
    }));
    return [
      ...columns,
      {
        title: "Action",
        key: "action",
        width: 80,
        render: (_, record) => (
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveRecipient(record.email)} />
        ),
      },
    ];
  };

  const showModal = () => setIsModalVisible(true);
  const handleModalOk = () => setIsModalVisible(false);
  const handleModalCancel = () => setIsModalVisible(false);

  // Variable management
  const handleAddVariable = () => {
    setEditingVariable(null);
    setVariableModalVisible(true);
  };

  const handleEditVariable = (variable) => {
    setEditingVariable(variable);
    setVariableModalVisible(true);
  };

  const handleSaveVariable = (variable) => {
    const newVars = editingVariable
      ? values.variables.map((v) => (v.key === editingVariable.key ? variable : v))
      : [...values.variables, variable];
    handleChange("variables", newVars);
    setVariableModalVisible(false);
    setEditingVariable(null);
  };

  const handleDeleteVariable = (key) => {
    handleChange("variables", values.variables.filter((v) => v.key !== key));
  };

  // Form submission
  const handleSubmit = async () => {
    // Validation
    if (!values.name) {
      message.error("Please enter a report name");
      return;
    }
    if (!values.htmlTemplate) {
      message.error("Please select an HTML template");
      return;
    }
    if (!values.emailSubject) {
      message.error("Please enter an email subject");
      return;
    }
    // Validate external recipients for external target type
    if (values.targetType === "external" && (!values.externalRecipients || values.externalRecipients.length === 0)) {
      message.error("Please add external recipients for this report");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: values.name,
        description: values.description,
        reportType: values.reportType,
        htmlTemplate: values.htmlTemplate,
        targetType: values.targetType,
        includedPlans: values.targetType === "planBased" ? values.includedPlans : [],
        excludedUsers: excludedUsers,
        targetedUsers: values.targetType === "targeted" ? values.targetedUsers : [],
        externalRecipients: values.targetType === "external" ? values.externalRecipients : [],
        schedule: {
          type: values.scheduleType,
          dayOfWeek: values.scheduleType === "weekly" ? values.dayOfWeek : undefined,
          dayOfMonth: values.scheduleType === "monthly" ? values.dayOfMonth : undefined,
          timeOfDay: {
            hour: values.timeHour,
            minute: values.timeMinute,
          },
          timezone: values.timezone,
        },
        dataSections: {
          billing: { enabled: values.billingEnabled },
          usage: { enabled: values.usageEnabled },
          performance: { enabled: values.performanceEnabled },
          analytics: { enabled: values.analyticsEnabled },
          team: { enabled: values.teamEnabled },
        },
        emailSettings: {
          subject: values.emailSubject,
          fromName: values.fromName,
          replyTo: values.replyTo,
          emailsToUse: values.emailsToUse,
          cc: values.cc,
          bcc: values.bcc,
          sendRate: values.emailSendingConfig?.emailsPerMinute || 20,
          batchSize: values.emailSendingConfig?.batchSize || 50,
        },
        variables: values.variables,
      };

      await createPulseReport(payload);
      SuccessNotification("Pulse report created successfully!");
      history.push("/super-admin/pulse-reports/list");
    } catch (error) {
      console.error("Error creating pulse report:", error);
      ErrorNotification(error.response?.data?.message || "Failed to create pulse report");
    } finally {
      setLoading(false);
    }
  };

  const selectedDetails = getSelectedDetails();

  // Data section descriptions
  const dataSectionInfo = {
    billing: {
      title: "Billing/Invoice Data",
      description: "Include billing details like plan name, amount, payment date, and invoice links",
    },
    usage: {
      title: "Usage Metrics",
      description: "Show chat consumption - allotted, used, remaining chats and usage percentage",
    },
    performance: {
      title: "Chatbot Performance",
      description: "Include chatbot stats like total conversations, resolution rate, avg response time",
    },
    analytics: {
      title: "Analytics Data",
      description: "Detailed analytics including visitor data, engagement metrics, and trends",
    },
    team: {
      title: "Team Data",
      description: "Team member information, roles, and collaborative workspace stats",
    },
  };

  return (
    <Row gutter={24}>
      <Col span={16}>
        <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          {/* Start from Template Banner */}
          <Form.Item wrapperCol={{ span: 18, offset: 6 }}>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderLeft: "4px solid #722ed1",
                borderRadius: 8,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#f3e8ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppstoreOutlined style={{ fontSize: 20, color: "#722ed1" }} />
                </div>
                <div>
                  <Typography.Text strong style={{ fontSize: 14, display: "block", color: "#1e293b" }}>
                    {selectedTemplateName ? `Using: ${selectedTemplateName}` : "Start with a template"}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 13, color: "#64748b" }}>
                    24 pulse report templates from Stripe, HubSpot, Mixpanel & more
                  </Typography.Text>
                </div>
              </div>
              <Button
                type="primary"
                onClick={() => setTemplateGalleryVisible(true)}
                style={{
                  borderRadius: 6,
                  background: "#722ed1",
                  borderColor: "#722ed1",
                }}
              >
                Browse Templates
              </Button>
            </div>
          </Form.Item>

          {/* Target Audience Section */}
          <Divider orientation="left" style={{ color: "#1890ff", borderColor: "#1890ff" }}>
            <UserOutlined style={{ marginRight: 8 }} />
            Target Audience
          </Divider>

          <Form.Item
            label="Target Type"
            required
            tooltip={{
              title: "Choose who receives this report. 'All Users' sends to everyone, 'Plan Based' targets specific subscription plans, 'External' lets you import CSV/Excel recipients.",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Select
              value={values.targetType}
              onChange={(v) => handleChange("targetType", v)}
              placeholder="Select target audience"
            >
              {targetTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  <Tooltip title={type.helper} placement="right">
                    <span>{type.label} - {type.description}</span>
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {values.targetType === "planBased" && (
            <Form.Item label="Include Plans" required>
              <Select
                mode="multiple"
                value={values.includedPlans}
                onChange={(v) => handleChange("includedPlans", v)}
                placeholder="Select plans to include"
              >
                {plans.map((plan) => (
                  <Option key={plan._id} value={plan._id}>
                    {plan.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {values.targetType === "targeted" && (
            <Form.Item label="Target Users" required>
              <Select
                mode="multiple"
                value={values.targetedUsers}
                onChange={(v) => handleChange("targetedUsers", v)}
                onSearch={handleUserSearch}
                loading={searching}
                filterOption={false}
                placeholder="Search and select users"
              >
                {userOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {values.targetType === "external" && (
            <Form.Item label="External Recipients" required>
              <Card style={{ borderColor: "#13c2c2" }}>
                <Space direction="vertical" style={{ width: "100%" }} size="large">
                  {/* Manual Entry Section */}
                  <Card size="small" title={<span style={{ color: "#13c2c2" }}><TeamOutlined style={{ marginRight: 8 }} />Add Recipients Manually</span>}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Input.TextArea
                        placeholder="Enter email addresses (one per line or separated by commas)"
                        rows={4}
                        onBlur={(e) => {
                          const text = e.target.value;
                          const emails = text.split(/[\n,]/).map((email) => email.trim()).filter((email) => email && isValidEmail(email));
                          if (emails.length > 0) {
                            const newRecipients = emails.map((email) => ({ email: email.toLowerCase(), values: {} }));
                            setValues((prev) => ({
                              ...prev,
                              externalRecipients: [
                                ...prev.externalRecipients,
                                ...newRecipients.filter((newRec) => !prev.externalRecipients.some((existingRec) => existingRec.email === newRec.email)),
                              ],
                            }));
                            e.target.value = "";
                            message.success(`${emails.length} recipient(s) added`);
                          }
                        }}
                      />
                      <Text type="secondary">Tip: You can paste multiple email addresses separated by commas or new lines</Text>
                    </Space>
                  </Card>

                  {/* File Upload Section */}
                  <Card size="small" title={<span style={{ color: "#722ed1" }}><InboxOutlined style={{ marginRight: 8 }} />Import Recipients from File</span>}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Alert
                        message={<span style={{ color: "#000" }}>Import Recipients</span>}
                        description={
                          <ul style={{ margin: "0", paddingLeft: "20px", color: "#000" }}>
                            <li>Upload CSV, Excel (.xlsx, .xls), or text files to import recipients</li>
                            <li>All columns from the files will be preserved as variables</li>
                            <li>The email column is required and must contain valid email addresses</li>
                            <li>Additional columns can be used as variables in your email template</li>
                          </ul>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16, backgroundColor: "#e6f7ff", border: "1px solid #91d5ff" }}
                      />

                      <Dragger
                        name="file"
                        multiple={true}
                        beforeUpload={() => false}
                        onChange={handleFileUpload}
                        accept=".csv,.txt,.xlsx,.xls"
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag files to this area to upload</p>
                        <p className="ant-upload-hint">Support for CSV, Excel, or text files with recipients</p>
                      </Dragger>
                    </Space>
                  </Card>

                  {values.externalRecipients?.length > 0 && (
                    <Card size="small" style={{ background: "#f6ffed", borderColor: "#b7eb8f" }}>
                      <Space>
                        <Badge count={values.externalRecipients.length} style={{ backgroundColor: "#52c41a" }} />
                        <Text strong>recipients loaded</Text>
                        <Button danger onClick={() => { setValues({ ...values, externalRecipients: [] }); setFileRecipients({}); }}>
                          Clear All
                        </Button>
                        <Button type="primary" onClick={showModal}>
                          View & Edit Recipients
                        </Button>
                      </Space>
                    </Card>
                  )}
                </Space>
              </Card>

              {/* External Recipients Modal */}
              <Modal
                title="Recipient List"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={1200}
                style={{ top: 20 }}
                footer={[<Button key="back" onClick={handleModalCancel}>Close</Button>]}
              >
                <div style={{ marginBottom: 24 }}>
                  <Card title="Add New Recipient" size="small">
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                      {Array.from(recipientColumns).map((column) => {
                        const columnDef = availableColumns.find((col) => col.key === column);
                        return (
                          <Form.Item key={column} label={columnDef?.title || column} style={{ margin: 0, minWidth: 200, flex: "1 1 200px", maxWidth: 300 }} required={columnDef?.required}>
                            <Input
                              value={newRecipientData[column]}
                              onChange={(e) => setNewRecipientData((prev) => ({ ...prev, [column]: e.target.value }))}
                              placeholder={`Enter ${columnDef?.title || column}`}
                              status={column === "email" && newRecipientData.email && !isValidEmail(newRecipientData.email) ? "error" : ""}
                            />
                          </Form.Item>
                        );
                      })}
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRecipient} style={{ marginBottom: 0 }}>Add Recipient</Button>
                    </div>
                  </Card>
                </div>
                <Table columns={getTableColumns()} dataSource={values.externalRecipients} rowKey="email" scroll={{ x: "max-content", y: 400 }} pagination={{ pageSize: 10 }} size="small" />
              </Modal>
            </Form.Item>
          )}

          {values.targetType !== "targeted" && values.targetType !== "external" && (
            <Form.Item label="Exclude Users">
              <Select
                mode="multiple"
                value={excludedUsers}
                onChange={setExcludedUsers}
                onSearch={handleUserSearch}
                loading={searching}
                filterOption={false}
                placeholder="Search and select users to exclude"
              >
                {userOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Report Details Section */}
          <Divider orientation="left" style={{ color: "#52c41a", borderColor: "#52c41a" }}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Report Details
          </Divider>

          <Form.Item
            label="Report Name"
            required
            tooltip={{
              title: "A descriptive name for this report campaign. This appears in the dashboard and reports list. Examples: 'Monthly Invoice Report', 'Weekly Performance Digest'",
              icon: <InfoCircleOutlined />,
            }}
          >
              <Input
                value={values.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., Monthly Invoice Report"
              />
            </Form.Item>

            <Form.Item
              label="Description"
              tooltip={{
                title: "Optional internal description for your reference. Helps you remember the purpose of this report.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <TextArea
                rows={2}
                value={values.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description of this pulse report"
              />
            </Form.Item>

            <Form.Item
              label="Report Type"
              required
              tooltip={{
                title: "Determines default data sections and recommended schedules. Invoice reports work best with on_refill, Usage/Performance with weekly/monthly schedules.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                value={values.reportType}
                onChange={(v) => handleChange("reportType", v)}
              >
                {reportTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    <Tooltip title={type.helper} placement="right">
                      <Space>
                        <span>{type.label}</span>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          - {type.description}
                        </Text>
                      </Space>
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="HTML Template"
              required
              tooltip={{
                title: "The email template used for this report. Templates use {{variable}} syntax for dynamic content. MJML templates are automatically converted to responsive HTML.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  value={values.htmlTemplate}
                  onChange={(v) => handleChange("htmlTemplate", v)}
                  placeholder="Select an HTML template"
                  showSearch
                  optionFilterProp="children"
                  style={{ flex: 1 }}
                >
                  {templates.map((template) => (
                    <Option key={template._id} value={template._id}>
                      {template.name}
                    </Option>
                  ))}
                </Select>
                <Tooltip title="Preview template">
                  <Button
                    icon={<EyeOutlined />}
                    onClick={handleTemplatePreview}
                    disabled={!values.htmlTemplate}
                  >
                    Preview
                  </Button>
                </Tooltip>
              </Space.Compact>
              <Button
                type="link"
                size="small"
                onClick={() => history.push("/super-admin/pulse-reports/html-templates")}
                style={{ paddingLeft: 0 }}
              >
                Manage HTML Templates
              </Button>
            </Form.Item>

          {/* Schedule Configuration Section */}
          <Divider orientation="left" style={{ color: "#722ed1", borderColor: "#722ed1" }}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Schedule Configuration
          </Divider>

          <Form.Item
            label="Schedule Type"
            required
            tooltip={{
              title: "When should this report be sent? 'On Refill' triggers after billing, 'Weekly/Monthly' runs on a schedule, 'Manual' only sends when you click 'Send Now'.",
              icon: <InfoCircleOutlined />,
            }}
          >
              <Select
                value={values.scheduleType}
                onChange={(v) => handleChange("scheduleType", v)}
              >
                {scheduleTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    <Tooltip title={type.helper} placement="right">
                      <Space>
                        <ClockCircleOutlined />
                        <span>{type.label}</span>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          - {type.description}
                        </Text>
                      </Space>
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {values.scheduleType === "weekly" && (
              <Form.Item label="Day of Week">
                <Select
                  value={values.dayOfWeek}
                  onChange={(v) => handleChange("dayOfWeek", v)}
                >
                  {daysOfWeek.map((day) => (
                    <Option key={day.value} value={day.value}>
                      {day.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {values.scheduleType === "monthly" && (
              <Form.Item label="Day of Month">
                <InputNumber
                  min={1}
                  max={28}
                  value={values.dayOfMonth}
                  onChange={(v) => handleChange("dayOfMonth", v)}
                />
              </Form.Item>
            )}

            {values.scheduleType !== "manual" && values.scheduleType !== "on_refill" && (
              <>
                <Form.Item label="Send Time">
                  <Space>
                    <InputNumber
                      min={0}
                      max={23}
                      value={values.timeHour}
                      onChange={(v) => handleChange("timeHour", v)}
                      placeholder="Hour"
                      style={{ width: 80 }}
                    />
                    <span>:</span>
                    <InputNumber
                      min={0}
                      max={59}
                      value={values.timeMinute}
                      onChange={(v) => handleChange("timeMinute", v)}
                      placeholder="Min"
                      style={{ width: 80 }}
                    />
                  </Space>
                </Form.Item>

                <Form.Item label="Timezone">
                  <Select
                    showSearch
                    value={values.timezone}
                    onChange={(v) => handleChange("timezone", v)}
                    filterOption={(input, option) =>
                      option.children.toString().toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                  >
                    {timezones.map((tz) => (
                      <Option key={tz} value={tz}>
                        {formatTimezone(tz)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

          {/* Email Settings Section */}
          <Divider orientation="left" style={{ color: "#fa8c16", borderColor: "#fa8c16" }}>
            <MailOutlined style={{ marginRight: 8 }} />
            Email Settings
          </Divider>
            <Form.Item
              label="Email Subject"
              required
              tooltip={{
                title: "Subject line supports variables! Use {{user_name}}, {{billing_amount}}, {{current_month}}, etc. Example: 'Your {{current_month}} Invoice - {{billing_amount}}'",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                value={values.emailSubject}
                onChange={(e) => handleChange("emailSubject", e.target.value)}
                placeholder="e.g., Your {{current_month}} Invoice from Copilotly"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Use {`{{variable_name}}`} for dynamic content. Example: "Hi {`{{user_name}}`}, your {`{{billing_plan}}`} invoice"
              </Text>
            </Form.Item>

            <Form.Item
              label="From Name"
              tooltip={{
                title: "The sender name recipients see. Use your brand name or a friendly name like 'Copilotly Billing' or 'Copilotly Team'.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                value={values.fromName}
                onChange={(e) => handleChange("fromName", e.target.value)}
                placeholder="Copilotly"
              />
            </Form.Item>

            <Form.Item
              label="Reply To"
              tooltip={{
                title: "Optional reply-to address. When users reply to the email, it goes to this address instead of the sending address.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input
                value={values.replyTo}
                onChange={(e) => handleChange("replyTo", e.target.value)}
                placeholder="support@copilotly.com"
              />
            </Form.Item>

            {/* Sending Emails Selection */}
            <Form.Item
              label="Sending Emails"
              tooltip={{
                title: "Select email addresses to send this report from. Using multiple addresses can improve delivery rates.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Card size="small">
                <Select
                  mode="multiple"
                  style={{ width: "100%", marginBottom: 16 }}
                  placeholder="Select emails and aliases to send from..."
                  value={selectedEmails}
                  onChange={handleEmailSelection}
                  optionLabelProp="label"
                  listHeight={400}
                  maxTagCount={5}
                >
                  {Object.values(availableEmails).flat().map((email) => (
                    <React.Fragment key={email._id}>
                      <Option value={`email:${email._id}`} label={email.email}>
                        <Space>
                          <MailOutlined />
                          <span>{email.name || email.email.split("@")[0]}</span>
                          <Text type="secondary">&lt;{email.email}&gt;</Text>
                        </Space>
                      </Option>
                      {(email.aliases || []).map((alias) => (
                        <Option key={`alias:${email._id}:${alias}`} value={`alias:${email._id}:${alias}`} label={alias}>
                          <Space style={{ paddingLeft: 24 }}>
                            <UserOutlined />
                            <span>{alias.split("@")[0]}</span>
                            <Text type="secondary">&lt;{alias}&gt;</Text>
                            <Badge
                              count="Alias"
                              style={{
                                backgroundColor: "#e6f4ff",
                                color: "#1890ff",
                                fontSize: "12px",
                              }}
                            />
                          </Space>
                        </Option>
                      ))}
                    </React.Fragment>
                  ))}
                </Select>

                {selectedEmails.length > 0 ? (
                  <Card
                    size="small"
                    bordered={false}
                    style={{ background: "#f5f5f5" }}
                  >
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      <Space>
                        <Badge count={selectedDetails.totalEmails} style={{ backgroundColor: "#1890ff" }} />
                        <Text>Total sending addresses</Text>
                      </Space>
                      <Space>
                        <Badge count={selectedDetails.baseEmails} style={{ backgroundColor: "#52c41a" }} />
                        <Text>Primary emails</Text>
                      </Space>
                      <Space>
                        <Badge count={selectedDetails.aliases} style={{ backgroundColor: "#722ed1" }} />
                        <Text>Aliases</Text>
                      </Space>
                      <Space>
                        <Badge count={selectedDetails.domains.size} style={{ backgroundColor: "#faad14" }} />
                        <Text>Unique domains</Text>
                      </Space>
                    </Space>
                  </Card>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Text type="secondary">No sending emails selected</Text>
                    }
                  />
                )}
              </Card>
            </Form.Item>

            {/* CC */}
            <Form.Item label="CC">
              <Input
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                onPressEnter={handleCcAdd}
                placeholder="Enter emails, press Enter to add"
              />
              {values.cc.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {values.cc.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleChange("cc", values.cc.filter((e) => e !== email))}
                      style={{ margin: 4 }}
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              )}
            </Form.Item>

            {/* BCC */}
            <Form.Item label="BCC">
              <Input
                value={bccInput}
                onChange={(e) => setBccInput(e.target.value)}
                onPressEnter={handleBccAdd}
                placeholder="Enter emails, press Enter to add"
              />
              {values.bcc.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {values.bcc.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleChange("bcc", values.bcc.filter((e) => e !== email))}
                      style={{ margin: 4 }}
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              )}
            </Form.Item>

            {/* Email Volume Control */}
            <Form.Item label="Sending Volume" tooltip="Control the sending rate for this report">
              <EmailVolumeControl
                totalRecipients={0}
                initialMinutesBetweenEmails={values.emailSendingConfig?.minutesBetweenEmails || 5}
                emailsPerHour={values.emailSendingConfig?.emailsPerHour || 12}
                senderCount={selectedEmails.length || 1}
                initialSendingMode={values.emailSendingConfig?.sendingMode || "time_based"}
                initialEmailsPerMinute={values.emailSendingConfig?.emailsPerMinute || 20}
                onVolumeChange={(config) => {
                  handleChange("emailSendingConfig", {
                    ...values.emailSendingConfig,
                    ...config,
                  });
                }}
              />
            </Form.Item>

          {/* Custom Variables Section */}
          <Divider orientation="left" style={{ color: "#13c2c2", borderColor: "#13c2c2" }}>
            <CodeOutlined style={{ marginRight: 8 }} />
            Custom Variables
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddVariable}
              style={{ marginLeft: 16 }}
            >
              Add Variable
            </Button>
          </Divider>
            {values.variables.length > 0 ? (
              <Table
                size="small"
                dataSource={values.variables}
                rowKey="key"
                pagination={false}
                columns={[
                  {
                    title: "Key",
                    dataIndex: "key",
                    render: (key) => <code>{`{{${key}}}`}</code>,
                  },
                  {
                    title: "Default Value",
                    dataIndex: "defaultValue",
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                  },
                  {
                    title: "Actions",
                    render: (_, record) => (
                      <Space>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditVariable(record)}
                        />
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteVariable(record.key)}
                        />
                      </Space>
                    ),
                  },
                ]}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" align="center">
                    <Text type="secondary">No custom variables defined</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Add variables to personalize your report template
                    </Text>
                  </Space>
                }
              />
            )}

            <Alert
              type="info"
              showIcon
              message={<span style={{ color: "#000" }}>Using Variables</span>}
              description={
                <span style={{ color: "#000" }}>
                  Use variables in your template with {`{{variable_key}}`} syntax.
                  Built-in variables are automatically available.
                </span>
              }
              style={{ marginTop: 16, backgroundColor: "#e6f7ff", border: "1px solid #91d5ff" }}
            />

          {/* Submit */}
          <Divider />
          <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                Create Pulse Report
              </Button>
              <Button onClick={() => history.push("/super-admin/pulse-reports/list")}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <VariableForm
          variable={variableModalVisible ? editingVariable : undefined}
          onSave={handleSaveVariable}
          onCancel={() => {
            setVariableModalVisible(false);
            setEditingVariable(null);
          }}
        />

        <PulseTemplateGallery
          visible={templateGalleryVisible}
          onClose={() => setTemplateGalleryVisible(false)}
          onSelect={handleTemplateSelect}
        />

        {/* HTML Template Preview Modal */}
        <Modal
          title={templatePreviewData?.name || "Template Preview"}
          open={templatePreviewVisible}
          onCancel={() => {
            setTemplatePreviewVisible(false);
            setTemplatePreviewData(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setTemplatePreviewVisible(false);
                setTemplatePreviewData(null);
              }}
            >
              Close
            </Button>,
          ]}
          width={900}
          bodyStyle={{ padding: 0, height: "70vh" }}
          centered
        >
          {templatePreviewLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Spin size="large" tip="Loading preview..." />
            </div>
          ) : templatePreviewData?.compiledHtml ? (
            <iframe
              srcDoc={templatePreviewData.compiledHtml}
              title="Template Preview"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "#999",
              }}
            >
              No preview available. Template may not have compiled HTML.
            </div>
          )}
        </Modal>
      </Col>

      {/* Right Sidebar - Data Sections & Variables */}
      <Col span={8}>
        {/* Quick Reference Guide */}
        <Card
          title={<span style={{ color: "#1890ff" }}><InfoCircleOutlined style={{ marginRight: 8 }} />Quick Reference Guide</span>}
          size="small"
          style={{ marginBottom: 16 }}
          headStyle={{ borderBottom: "2px solid #1890ff" }}
        >
          <Collapse size="small" ghost defaultActiveKey={[]}>
            <Panel header={<Text strong style={{ color: "#722ed1" }}>What is a Pulse Report?</Text>} key="what">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Unlike email campaigns (same content to everyone), Pulse Reports generate <strong>unique personalized content</strong> for each user based on their actual data - billing, usage, performance metrics, etc.
              </Text>
            </Panel>
            <Panel header={<Text strong style={{ color: "#52c41a" }}>When to Use Each Report Type</Text>} key="types">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0, color: "rgba(0,0,0,0.65)" }}>
                <li><strong>Invoice:</strong> After billing (on_refill)</li>
                <li><strong>Usage:</strong> Weekly usage updates</li>
                <li><strong>Performance:</strong> Weekly/monthly ROI</li>
                <li><strong>Analytics:</strong> Traffic insights</li>
                <li><strong>Digest:</strong> Combined summaries</li>
                <li><strong>Newsletter:</strong> Announcements</li>
              </ul>
            </Panel>
            <Panel header={<Text strong style={{ color: "#fa8c16" }}>Schedule Recommendations</Text>} key="schedule">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0, color: "rgba(0,0,0,0.65)" }}>
                <li><strong>Invoices:</strong> on_refill (auto after payment)</li>
                <li><strong>Usage:</strong> Weekly on Monday</li>
                <li><strong>Performance:</strong> Monthly on 1st</li>
                <li><strong>Newsletters:</strong> Manual (when ready)</li>
              </ul>
            </Panel>
            <Panel header={<Text strong style={{ color: "#13c2c2" }}>External Recipients Tips</Text>} key="external">
              <Text type="secondary" style={{ fontSize: 11 }}>
                CSV columns become variables! If your CSV has columns <code>email, name, company</code>, use <code>{"{{name}}"}</code> and <code>{"{{company}}"}</code> in your template.
              </Text>
            </Panel>
          </Collapse>
        </Card>

        {/* Data Sections */}
        <Card
          title={<span style={{ color: "#eb2f96" }}>Data Sections to Include</span>}
          size="small"
          headStyle={{ borderBottom: "2px solid #eb2f96" }}
        >
          <Alert
            type="info"
            showIcon
            message={<span style={{ color: "#000" }}>Select which data sections to include in the report. Only enabled sections will have their variables populated.</span>}
            style={{ marginBottom: 16, backgroundColor: "#e6f7ff", border: "1px solid #91d5ff" }}
          />

          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
              <div>
                <Text strong>{dataSectionInfo.billing.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{dataSectionInfo.billing.description}</Text>
              </div>
              <Switch checked={values.billingEnabled} onChange={(v) => handleChange("billingEnabled", v)} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f0f0" }}>
              <div>
                <Text strong>{dataSectionInfo.usage.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{dataSectionInfo.usage.description}</Text>
              </div>
              <Switch checked={values.usageEnabled} onChange={(v) => handleChange("usageEnabled", v)} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f0f0" }}>
              <div>
                <Text strong>{dataSectionInfo.performance.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{dataSectionInfo.performance.description}</Text>
              </div>
              <Switch checked={values.performanceEnabled} onChange={(v) => handleChange("performanceEnabled", v)} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f0f0" }}>
              <div>
                <Text strong>{dataSectionInfo.analytics.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{dataSectionInfo.analytics.description}</Text>
              </div>
              <Switch checked={values.analyticsEnabled} onChange={(v) => handleChange("analyticsEnabled", v)} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f0f0" }}>
              <div>
                <Text strong>{dataSectionInfo.team.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{dataSectionInfo.team.description}</Text>
              </div>
              <Switch checked={values.teamEnabled} onChange={(v) => handleChange("teamEnabled", v)} />
            </div>
          </Space>
        </Card>

        {/* Available Variables */}
        <Card
          title={<span style={{ color: "#13c2c2" }}>Available Template Variables</span>}
          size="small"
          style={{ marginTop: 16 }}
          headStyle={{ borderBottom: "2px solid #13c2c2" }}
        >
          <Alert
            type="success"
            showIcon
            message={<span style={{ fontSize: 11 }}>Use <code>{"{{variable_name}}"}</code> syntax in your HTML template and email subject</span>}
            style={{ marginBottom: 12, padding: "4px 8px" }}
          />
          <Collapse size="small" ghost style={{ marginTop: 8 }} defaultActiveKey={[]}>
            <Panel header={<span style={{ color: "#1890ff" }}>User Variables (Always Available)</span>} key="user">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>user_name</code> - Full name</li>
                <li><code>user_email</code> - Email address</li>
                <li><code>user_plan</code> - Current plan name</li>
                <li><code>user_joined_date</code> - Registration date</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#52c41a" }}>Billing Variables</span>} key="billing">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>billing_plan</code> - Plan name</li>
                <li><code>billing_amount</code> - Formatted amount (e.g., $29.00)</li>
                <li><code>billing_currency</code> - Currency code (USD)</li>
                <li><code>billing_period_start</code> - Period start date</li>
                <li><code>billing_period_end</code> - Period end date</li>
                <li><code>billing_next_renewal</code> - Next billing date</li>
                <li><code>billing_payment_method</code> - Card ending (Visa *4242)</li>
                <li><code>billing_invoice_url</code> - Stripe invoice link</li>
                <li><code>billing_pdf_url</code> - Invoice PDF link</li>
                <li><code>billing_total_spent</code> - Lifetime total</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#fa8c16" }}>Usage Variables</span>} key="usage">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>usage_chats_allotted</code> - Total allowed</li>
                <li><code>usage_chats_used</code> - Consumed count</li>
                <li><code>usage_chats_remaining</code> - Left to use</li>
                <li><code>usage_percentage</code> - Usage % (e.g., 75%)</li>
                <li><code>usage_storage_used</code> - Storage in MB</li>
                <li><code>usage_storage_limit</code> - Storage limit</li>
                <li><code>usage_change_percent</code> - vs last period</li>
                <li><code>usage_trend</code> - up/down/stable</li>
                <li><code>usage_trend_icon</code> - Arrow icon</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#722ed1" }}>Performance Variables</span>} key="performance">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>perf_total_chatbots</code> - Bot count</li>
                <li><code>perf_active_chatbots</code> - Active bots</li>
                <li><code>perf_total_conversations</code> - All time</li>
                <li><code>perf_conversations_period</code> - This period</li>
                <li><code>perf_avg_duration</code> - Avg chat time</li>
                <li><code>perf_top_bot_name</code> - Best performer</li>
                <li><code>perf_conversion_rate</code> - Conversion %</li>
                <li><code>perf_satisfaction</code> - CSAT score</li>
                <li><code>perf_trend</code> - up/down/stable</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#eb2f96" }}>Analytics Variables</span>} key="analytics">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>analytics_total_visitors</code> - Total visits</li>
                <li><code>analytics_unique_visitors</code> - Unique</li>
                <li><code>analytics_top_source_1</code> - #1 source</li>
                <li><code>analytics_top_source_2</code> - #2 source</li>
                <li><code>analytics_top_country_1</code> - #1 country</li>
                <li><code>analytics_desktop_percent</code> - Desktop %</li>
                <li><code>analytics_mobile_percent</code> - Mobile %</li>
                <li><code>analytics_peak_hours</code> - Busiest times</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#13c2c2" }}>Team Variables</span>} key="team">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>team_member_count</code> - Total members</li>
                <li><code>team_active_members</code> - Active count</li>
                <li><code>team_tickets_handled</code> - Support tickets</li>
                <li><code>team_avg_response_time</code> - Avg response</li>
                <li><code>team_top_member_1</code> - Top performer</li>
              </ul>
            </Panel>
            <Panel header={<span style={{ color: "#8c8c8c" }}>Meta Variables (Always Available)</span>} key="meta">
              <ul style={{ fontSize: 11, paddingLeft: 16, margin: 0 }}>
                <li><code>report_generated_at</code> - Date generated</li>
                <li><code>report_generated_time</code> - Time generated</li>
                <li><code>report_type</code> - Report type name</li>
                <li><code>report_name</code> - Report campaign name</li>
                <li><code>current_year</code> - Current year (2024)</li>
                <li><code>current_month</code> - Current month name</li>
              </ul>
            </Panel>
          </Collapse>
        </Card>
      </Col>
    </Row>
  );
};

export default withRouter(PulseReportCreateForm);
